import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';

const InpatientVitalSignsModal = ({ isOpen, onClose, inpatientId, onSave }) => {
  const [vitalSigns, setVitalSigns] = useState({
    serial: '',
    temperature: '',
    heartRate: '',
    respiratoryRate: '',
    capillaryRefillTime: '',
    bodyWeight: '',
    hydrationStatus: '',
    dateTime: new Date().toISOString().slice(0, 16),
    mucousMembraneColor: '',
    bodyConditionScore: '',
    painScore: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [errors, setErrors] = useState({});
  const [allRecords, setAllRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'form' | 'view'
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Load existing vital signs data when modal opens
  useEffect(() => {
    if (isOpen && inpatientId) {
      loadExistingVitalSigns();
    }
  }, [isOpen, inpatientId]);

  // Handle keyboard events and body scroll
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadExistingVitalSigns = async () => {
    if (!inpatientId) return;

    setLoadingExisting(true);
    try {
      const endpoint = `${import.meta.env.VITE_API_URL}/api/v1/vital-signs/inpatient-visit/${inpatientId}`;
        
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data && result.data.length > 0) {
          // Store all records
          setAllRecords(result.data);
        } else {
          setAllRecords([]);
        }
      } else {
        setAllRecords([]);
      }
    } catch (error) {
      console.error('Error loading existing vital signs:', error);
      setAllRecords([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !loading && !loadingExisting) {
      handleClose();
    }
  };

  const handleInputChange = (field, value) => {
    setVitalSigns(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleAddNew = () => {
    setVitalSigns({
      serial: '',
      temperature: '',
      heartRate: '',
      respiratoryRate: '',
      capillaryRefillTime: '',
      bodyWeight: '',
      hydrationStatus: '',
      dateTime: new Date().toISOString().slice(0, 16),
      mucousMembraneColor: '',
      bodyConditionScore: '',
      painScore: ''
    });
    setEditingRecord(null);
    setViewMode('form');
  };

  const handleEdit = (record) => {
    setVitalSigns({
      serial: record.serial || '',
      temperature: record.temperature ? record.temperature.replace('°C', '') : '',
      heartRate: record.heart_rate ? record.heart_rate.replace(' bpm', '') : '',
      respiratoryRate: record.respiratory_rate ? record.respiratory_rate.replace(' breaths/min', '') : '',
      capillaryRefillTime: record.capillary_refill_time || '',
      bodyWeight: record.body_weight || '',
      hydrationStatus: record.hydration_status || '',
      dateTime: record.created_at ? new Date(record.created_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
      mucousMembraneColor: record.mucous_membrane_color || '',
      bodyConditionScore: record.body_condition_score || '',
      painScore: record.pain_scale || ''
    });
    setEditingRecord(record);
    setViewMode('form');
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setViewMode('view');
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this vital signs record?')) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/vital-signs/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        showSuccessToast('Vital signs record deleted successfully!');
        await loadExistingVitalSigns();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showErrorToast(errorData.message || 'Failed to delete record');
      }
    } catch (error) {
      console.error('Error deleting vital signs:', error);
      showErrorToast('Failed to delete record');
    }
  };

  const handleCancelForm = () => {
    setViewMode('table');
    setEditingRecord(null);
    setVitalSigns({
      serial: '',
      temperature: '',
      heartRate: '',
      respiratoryRate: '',
      capillaryRefillTime: '',
      bodyWeight: '',
      hydrationStatus: '',
      dateTime: new Date().toISOString().slice(0, 16),
      mucousMembraneColor: '',
      bodyConditionScore: '',
      painScore: ''
    });
    setErrors({});
  };

  const handleSave = async () => {
    // Enhanced validation
    const newErrors = {};

    // if (!vitalSigns.temperature) newErrors.temperature = 'Temperature is required';
    // if (!vitalSigns.heartRate) newErrors.heartRate = 'Heart rate is required';
    // if (!vitalSigns.respiratoryRate) newErrors.respiratoryRate = 'Respiratory rate is required';

    // Range validation
    // if (vitalSigns.temperature && (parseFloat(vitalSigns.temperature) < 30 || parseFloat(vitalSigns.temperature) > 45)) {
    //   newErrors.temperature = 'Temperature must be between 30°C and 45°C';
    // }
    // if (vitalSigns.heartRate && (parseInt(vitalSigns.heartRate) < 40 || parseInt(vitalSigns.heartRate) > 200)) {
    //   newErrors.heartRate = 'Heart rate must be between 40 and 200 BPM';
    // }
    // if (vitalSigns.respiratoryRate && (parseInt(vitalSigns.respiratoryRate) < 10 || parseInt(vitalSigns.respiratoryRate) > 60)) {
    //   newErrors.respiratoryRate = 'Respiratory rate must be between 10 and 60 BPM';
    // }

    // if (Object.keys(newErrors).length > 0) {
    //   setErrors(newErrors);
    //   showErrorToast('Please correct the errors before saving');
    //   return;
    // }

    // Require at least one field to be provided before saving
    const fieldsToCheck = [
      vitalSigns.serial,
      vitalSigns.temperature,
      vitalSigns.heartRate,
      vitalSigns.respiratoryRate,
      vitalSigns.capillaryRefillTime,
      vitalSigns.bodyWeight,
      vitalSigns.hydrationStatus,
      vitalSigns.mucousMembraneColor,
      vitalSigns.bodyConditionScore,
      vitalSigns.painScore
    ];

    const hasAtLeastOneValue = fieldsToCheck.some((value) => {
      return value !== undefined && value !== null && String(value).trim() !== '';
    });

    if (!hasAtLeastOneValue) {
      showErrorToast('Please fill at least one field before saving');
      return;
    }

    setLoading(true);
    try {
      // Prepare the payload for API
      const payload = {
        serial: vitalSigns.serial || '',
        temperature: vitalSigns.temperature ? `${parseFloat(vitalSigns.temperature)}°C` : '',
        heart_rate: vitalSigns.heartRate ? `${parseInt(vitalSigns.heartRate)} bpm` : '',
        respiratory_rate: vitalSigns.respiratoryRate ? `${parseInt(vitalSigns.respiratoryRate)} breaths/min` : '',
        capillary_refill_time: vitalSigns.capillaryRefillTime || '',
        body_weight: parseFloat(vitalSigns.bodyWeight) || 0,
        mucous_membrane_color: vitalSigns.mucousMembraneColor || '',
        body_condition_score: vitalSigns.bodyConditionScore || '',
        hydration_status: vitalSigns.hydrationStatus || '',
        pain_scale: parseInt(vitalSigns.painScore) || 0,
        inpatient_visit_id: parseInt(inpatientId)
      };


      const isEditing = editingRecord !== null;
      const url = isEditing 
        ? `${import.meta.env.VITE_API_URL}/api/v1/vital-signs/${editingRecord.id}`
        : `${import.meta.env.VITE_API_URL}/api/v1/vital-signs`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          showSuccessToast(isEditing ? 'Vital signs updated successfully!' : 'Vital signs saved successfully!');
          onSave && onSave(result.data);
          await loadExistingVitalSigns();
          handleCancelForm();
        } else {
          showErrorToast(result.message || 'Failed to save vital signs');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showErrorToast(errorData.message || response.statusText || 'Failed to save vital signs');
      }
    } catch (error) {
      console.error('Error saving vital signs:', error);
      showErrorToast('Failed to save vital signs');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setVitalSigns({
      serial: '',
      temperature: '',
      heartRate: '',
      respiratoryRate: '',
      capillaryRefillTime: '',
      bodyWeight: '',
      hydrationStatus: '',
      dateTime: new Date().toISOString().slice(0, 16),
      mucousMembraneColor: '',
      bodyConditionScore: '',
      painScore: ''
    });
    setErrors({});
    setLoadingExisting(false);
    setViewMode('table');
    setEditingRecord(null);
    setAllRecords([]);
    onClose();
  };

  const getPainScaleColor = (score) => {
    const num = parseInt(score) || 0;
    if (num <= 1) return '#10b981'; // Green
    if (num <= 3) return '#f59e0b'; // Yellow
    if (num <= 5) return '#f97316'; // Orange
    if (num <= 7) return '#ef4444'; // Red
    return '#dc2626'; // Dark red
  };

  const getPainScaleEmoji = (score) => {
    const num = parseInt(score) || 0;
    if (num <= 1) return '😊';
    if (num <= 3) return '🙂';
    if (num <= 5) return '😐';
    if (num <= 7) return '😟';
    if (num <= 9) return '😢';
    return '😭';
  };

  const getPainScaleLabel = (score) => {
    const num = parseInt(score) || 0;
    if (num <= 1) return 'NO PAIN';
    if (num <= 3) return 'MILD PAIN';
    if (num <= 5) return 'MODERATE PAIN';
    if (num <= 7) return 'SEVERE PAIN';
    if (num <= 9) return 'VERY SEVERE PAIN';
    return 'WORST PAIN POSSIBLE';
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={`vital-signs-modal ${isOpen ? 'modal-open' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'grid',
        placeItems: 'center',
        zIndex: 10000,
        padding: '16px',
        fontFamily: '"Red Hat Display", sans-serif'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: 'min(1100px, 95vw)',
          width: 'min(1100px, 95vw)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isOpen
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            : '0 0 0 0 rgba(0, 0, 0, 0)',
          border: isOpen ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid transparent',
          transform: isOpen
            ? 'scale(1) translateY(0px)'
            : 'scale(0.95) translateY(20px)',
          opacity: isOpen ? 1 : 0,
          transition: isOpen
            ? 'all 0.3s ease-out'
            : 'all 0.2s ease-in',
          transformOrigin: 'center center'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #EBF8FF 0%, #F0F9FF 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 600,
                color: '#1F2937',
                margin: 0
              }}>
                {loadingExisting
                  ? 'Loading Vital Signs...'
                  : viewMode === 'form'
                    ? (editingRecord ? 'Edit Vital Signs' : 'Add Vital Signs')
                    : viewMode === 'view'
                      ? 'Vital Signs Details'
                      : 'Vital Signs Records'}
              </h2>
              {loadingExisting && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '4px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid #2563EB',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <span style={{
                    fontSize: '12px',
                    color: '#6B7280'
                  }}>
                    Loading existing data...
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading || loadingExisting}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (loading || loadingExisting) ? 'not-allowed' : 'pointer',
              color: '#6B7280',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!loading && !loadingExisting) {
                e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                e.target.style.color = '#EF4444';
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !loadingExisting) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6B7280';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content Wrapper to preserve border radius on scrollbar side */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '8px' }}>
        
        {/* TABLE VIEW */}
        {viewMode === 'table' && (
          <div style={{ marginBottom: '32px' }}>
            {/* Always show the Add New button */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px' 
            }}>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                {loadingExisting ? 'Loading...' : `${allRecords.length} ${allRecords.length === 1 ? 'record' : 'records'} found`}
              </p>
              <button
                onClick={handleAddNew}
                disabled={loadingExisting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: loadingExisting ? '#93C5FD' : '#2563EB',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: loadingExisting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  if (!loadingExisting) {
                    e.target.style.backgroundColor = '#1D4ED8';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loadingExisting) {
                    e.target.style.backgroundColor = '#2563EB';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Add New Record
              </button>
            </div>

            {loadingExisting ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: '#F9FAFB',
                borderRadius: '12px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #E5E7EB',
                  borderTop: '4px solid #2563EB',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }} />
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                  Loading vital signs...
                </h3>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Please wait
                </p>
              </div>
            ) : allRecords.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: '#F9FAFB',
                borderRadius: '12px'
              }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}>
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#374151', margin: '0 0 8px 0' }}>
                  No vital signs records yet
                </h3>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
                  Click "Add New Record" to create the first vital signs entry
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #E5E7EB' }}>Date/Time</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #E5E7EB' }}>Temp (°C)</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #E5E7EB' }}>HR (bpm)</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #E5E7EB' }}>RR (bpm)</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #E5E7EB' }}>Weight (kg)</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #E5E7EB' }}>Pain</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '2px solid #E5E7EB' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRecords.map((record, index) => (
                      <tr 
                        key={record.id || index}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EFF6FF'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#FFFFFF' : '#F9FAFB'}
                      >
                        <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', color: '#374151' }}>
                          {record.created_at ? new Date(record.created_at).toLocaleString() : 'N/A'}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', color: '#374151' }}>
                          {record.temperature || '-'}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', color: '#374151' }}>
                          {record.heart_rate || '-'}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', color: '#374151' }}>
                          {record.respiratory_rate || '-'}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', color: '#374151' }}>
                          {record.body_weight || '-'}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', color: '#374151' }}>
                          {record.pain_scale ? `${record.pain_scale}/10` : '-'}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleView(record)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#10B981',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#059669';
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#10B981';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEdit(record)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#3B82F6',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#2563EB';
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#3B82F6';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(record.id)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#EF4444',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#DC2626';
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#EF4444';
                                e.target.style.transform = 'scale(1)';
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {/* FORM VIEW */}
        {viewMode === 'form' && (
        <div style={{ marginBottom: '32px' }}>
          {/* Vital Signs Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {/* Left Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
 
              {/* Temperature */}
              <div className="form-field">
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Temperature
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    step="0.1"
                    value={vitalSigns.temperature}
                    onChange={(e) => handleInputChange('temperature', e.target.value)}
                    placeholder=""
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: `1px solid ${errors.temperature ? '#EF4444' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#FFFFFF',
                      fontFamily: '"Red Hat Display", sans-serif',
                      MozAppearance: 'textfield'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.temperature ? '#EF4444' : '#2563EB';
                      e.target.style.boxShadow = errors.temperature ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(37, 99, 235, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.temperature ? '#EF4444' : '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6B7280',
                    fontSize: '14px'
                  }}>°C</span>
                </div>
                {errors.temperature && (
                  <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                    {errors.temperature}
                  </p>
                )}
              </div>

              {/* Heart Rate */}
              <div className="form-field">
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Heart Rate
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={vitalSigns.heartRate}
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange('heartRate', sanitized);
                    }}
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder=""
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: `1px solid ${errors.heartRate ? '#EF4444' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#FFFFFF',
                      fontFamily: '"Red Hat Display", sans-serif',
                      MozAppearance: 'textfield'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.heartRate ? '#EF4444' : '#2563EB';
                      e.target.style.boxShadow = errors.heartRate ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(37, 99, 235, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.heartRate ? '#EF4444' : '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6B7280',
                    fontSize: '12px'
                  }}>BPM</span>
                </div>
                {errors.heartRate && (
                  <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                    {errors.heartRate}
                  </p>
                )}
              </div>

              {/* Respiratory Rate */}
              <div className="form-field">
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Respiratory Rate
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    value={vitalSigns.respiratoryRate}
                    onChange={(e) => handleInputChange('respiratoryRate', e.target.value)}
                    placeholder=""
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: `1px solid ${errors.respiratoryRate ? '#EF4444' : '#D1D5DB'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#FFFFFF',
                      fontFamily: '"Red Hat Display", sans-serif',
                      MozAppearance: 'textfield'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.respiratoryRate ? '#EF4444' : '#2563EB';
                      e.target.style.boxShadow = errors.respiratoryRate ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : '0 0 0 3px rgba(37, 99, 235, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.respiratoryRate ? '#EF4444' : '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6B7280',
                    fontSize: '12px'
                  }}>BPM</span>
                </div>
                {errors.respiratoryRate && (
                  <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                    {errors.respiratoryRate}
                  </p>
                )}
              </div>


              {/* Capillary Refill Time */}
              <div className="form-field">
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Capillary Refill Time
                </label>
                <input
                  type="text"
                  value={vitalSigns.capillaryRefillTime}
                  onChange={(e) => handleInputChange('capillaryRefillTime', e.target.value)}
                  placeholder=""
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#FFFFFF',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563EB';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Right Column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
 
              {/* Body Weight */}
              <div className="form-field">
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Body Weight
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    step="0.1"
                    value={vitalSigns.bodyWeight}
                    onChange={(e) => handleInputChange('bodyWeight', e.target.value)}
                    placeholder=""
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#FFFFFF',
                      fontFamily: '"Red Hat Display", sans-serif',
                      MozAppearance: 'textfield'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563EB';
                      e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6B7280',
                    fontSize: '12px'
                  }}>kg</span>
                </div>
              </div>

              {/* Hydration Status */}
              <div className="form-field">
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Hydration Status
                </label>
                <input
                  type="text"
                  value={vitalSigns.hydrationStatus}
                  onChange={(e) => handleInputChange('hydrationStatus', e.target.value)}
                  placeholder=""
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#FFFFFF',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563EB';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Mucous Membrane Color */}
              <div className="form-field">
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Mucous Membrane Color
                </label>
                <input
                  type="text"
                  value={vitalSigns.mucousMembraneColor}
                  onChange={(e) => handleInputChange('mucousMembraneColor', e.target.value)}
                  placeholder=""
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#FFFFFF',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563EB';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Body Condition Score */}
              <div className="form-field">
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Body Condition Score
                </label>
                <input
                  type="text"
                  value={vitalSigns.bodyConditionScore}
                  onChange={(e) => handleInputChange('bodyConditionScore', e.target.value)}
                  placeholder=""
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#FFFFFF',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563EB';
                    e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D5DB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Pain Scale Section */}
          <div className="pain-scale-section" style={{
            backgroundColor: '#F8FAFC',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#1F2937',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#FEF3C7',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                😊
              </span>
              Pain Assessment Scale
            </h3>

            {/* Pain Score Input */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '8px'
              }}>
                Pain Score (0-10)
              </label>
              <input
                type="number"
                min="0"
                max="10"
                value={vitalSigns.painScore}
                onChange={(e) => handleInputChange('painScore', e.target.value)}
                placeholder=""
                style={{
                  width: '120px',
                  padding: '12px 16px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#FFFFFF',
                  fontFamily: '"Red Hat Display", sans-serif',
                  MozAppearance: 'textfield'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563EB';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#D1D5DB';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Visual Pain Scale */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
              gap: '8px',
              marginBottom: '16px'
            }}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                <button
                  key={score}
                  onClick={() => handleInputChange('painScore', score.toString())}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '12px 8px',
                    border: `2px solid ${parseInt(vitalSigns.painScore) === score ? getPainScaleColor(score.toString()) : '#E5E7EB'}`,
                    borderRadius: '8px',
                    backgroundColor: parseInt(vitalSigns.painScore) === score ? `${getPainScaleColor(score.toString())}20` : '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: '"Red Hat Display", sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    if (parseInt(vitalSigns.painScore) !== score) {
                      e.target.style.borderColor = getPainScaleColor(score.toString());
                      e.target.style.backgroundColor = `${getPainScaleColor(score.toString())}10`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (parseInt(vitalSigns.painScore) !== score) {
                      e.target.style.borderColor = '#E5E7EB';
                      e.target.style.backgroundColor = '#FFFFFF';
                    }
                  }}
                >
                  <div style={{ fontSize: '18px' }}>
                    {getPainScaleEmoji(score.toString())}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: parseInt(vitalSigns.painScore) === score ? '#FFFFFF' : '#374151'
                  }}>
                    {score}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: parseInt(vitalSigns.painScore) === score ? '#FFFFFF' : '#6B7280',
                    textAlign: 'center',
                    lineHeight: '1.2'
                  }}>
                    {score === 0 && 'No Pain'}
                    {score === 1 && 'Mild'}
                    {score === 2 && 'Mild'}
                    {score === 3 && 'Mild'}
                    {score === 4 && 'Moderate'}
                    {score === 5 && 'Moderate'}
                    {score === 6 && 'Severe'}
                    {score === 7 && 'Severe'}
                    {score === 8 && 'Very Severe'}
                    {score === 9 && 'Very Severe'}
                    {score === 10 && 'Worst'}
                  </div>
                </button>
              ))}
            </div>

            {/* Selected Pain Info */}
            {vitalSigns.painScore && (
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                padding: '16px',
                border: `1px solid ${getPainScaleColor(vitalSigns.painScore)}`
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '24px' }}>
                    {getPainScaleEmoji(vitalSigns.painScore)}
                  </span>
                  <div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#1F2937'
                    }}>
                      Score: {vitalSigns.painScore}/10
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6B7280'
                    }}>
                      {getPainScaleLabel(vitalSigns.painScore)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* VIEW DETAILS */}
        {viewMode === 'view' && selectedRecord && (
          <div style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Date/Time</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>
                  {selectedRecord.created_at ? new Date(selectedRecord.created_at).toLocaleString() : 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Temperature</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.temperature || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Heart Rate</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.heart_rate || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Respiratory Rate</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.respiratory_rate || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Capillary Refill Time</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.capillary_refill_time || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Body Weight</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.body_weight || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Hydration Status</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.hydration_status || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Mucous Membrane Color</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.mucous_membrane_color || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Body Condition Score</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.body_condition_score || '-'}</div>
              </div>
              <div>
                <div style={{ color: '#6B7280', fontSize: '12px' }}>Pain Scale</div>
                <div style={{ color: '#111827', fontWeight: 600 }}>{selectedRecord.pain_scale ? `${selectedRecord.pain_scale}/10` : '-'}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button
                onClick={() => handleEdit(selectedRecord)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#3B82F6',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(selectedRecord.id)}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#EF4444',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        )}
        </div>

        {/* Action Buttons */}
        <div className="action-buttons" style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          paddingTop: '24px',
          borderTop: '1px solid #E5E7EB'
        }}>
          {(viewMode === 'form' || viewMode === 'view') && (
            <button
              onClick={() => {
                setViewMode('table');
                setSelectedRecord(null);
                setEditingRecord(null);
              }}
              disabled={loading || loadingExisting}
              style={{
                padding: '12px 24px',
                backgroundColor: 'transparent',
                color: (loading || loadingExisting) ? '#9CA3AF' : '#374151',
                border: '1px solid #D1D5DB',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: (loading || loadingExisting) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: '"Red Hat Display", sans-serif',
                minWidth: '80px',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (!loading && !loadingExisting) {
                  e.target.style.backgroundColor = 'rgba(55, 65, 81, 0.05)';
                  e.target.style.borderColor = '#9CA3AF';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(55, 65, 81, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && !loadingExisting) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#D1D5DB';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              Back to List
            </button>
          )}
          <button
            onClick={viewMode === 'form' ? handleSave : handleClose}
            disabled={loading || loadingExisting}
            style={{
              padding: '12px 24px',
              backgroundColor: viewMode === 'form' ? ((loading || loadingExisting) ? '#93C5FD' : '#2563EB') : 'transparent',
              color: viewMode === 'form' ? '#FFFFFF' : ((loading || loadingExisting) ? '#9CA3AF' : '#374151'),
              border: viewMode === 'form' ? 'none' : '1px solid #D1D5DB',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: (loading || loadingExisting) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: '"Red Hat Display", sans-serif',
              minWidth: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: viewMode === 'form' && (loading || loadingExisting)
                ? 'none'
                : (viewMode === 'form' ? '0 4px 14px 0 rgba(37, 99, 235, 0.39)' : 'none')
            }}
            onMouseEnter={(e) => {
              if (!loading && !loadingExisting) {
                if (viewMode === 'form') {
                  e.target.style.backgroundColor = '#1D4ED8';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 8px 25px 0 rgba(37, 99, 235, 0.5)';
                } else {
                  e.target.style.backgroundColor = 'rgba(55, 65, 81, 0.05)';
                  e.target.style.borderColor = '#9CA3AF';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(55, 65, 81, 0.15)';
                }
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !loadingExisting) {
                if (viewMode === 'form') {
                  e.target.style.backgroundColor = '#2563EB';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 14px 0 rgba(37, 99, 235, 0.39)';
                } else {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = '#D1D5DB';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }
            }}
          >
            {viewMode === 'form' && loading && (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #FFFFFF',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            {viewMode === 'form' 
              ? ((loading || loadingExisting) ? (loading ? 'Saving...' : 'Loading...') : (editingRecord ? 'Update Vital Signs' : 'Save Vital Signs'))
              : 'Close'
            }
          </button>
        </div>

        {/* Add spinner animation and responsive styles */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(15px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes slideInFromBottom {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateX(-10px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }

            /* Hide number input spinners */
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }

            /* Modern scrollbar styling */
            ::-webkit-scrollbar {
              width: 8px;
              height: 8px;
            }

            ::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 0 8px 8px 0;
            }

            ::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 0 8px 8px 0;
              transition: background 0.2s ease;
            }

            ::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }

            ::-webkit-scrollbar-corner {
              background: #f1f5f9;
              border-radius: 0 0 8px 0;
            }

            /* Firefox scrollbar styling */
            * {
              scrollbar-width: thin;
              scrollbar-color: #cbd5e1 #f1f5f9;
            }

            /* Animation classes for all elements - formal timing */
            .form-field,
            .pain-scale-section,
            .action-buttons,
            .modal-header {
              animation-delay: 0.1s;
              opacity: 0;
            }

            .form-field {
              animation: fadeInUp 0.4s ease-out forwards;
            }

            .pain-scale-section {
              animation: slideInFromBottom 0.5s ease-out forwards;
            }

            .action-buttons {
              animation: fadeIn 0.3s ease-out forwards;
            }

            .modal-header {
              animation: slideIn 0.3s ease-out forwards;
            }

            /* Shimmer effect for modal opening */
            .vital-signs-modal.modal-open > div {
              position: relative;
              overflow: hidden;
            }
            /* Initially hide all animated elements */
            .vital-signs-modal:not(.modal-open) .form-field,
            .vital-signs-modal:not(.modal-open) .pain-scale-section,
            .vital-signs-modal:not(.modal-open) .action-buttons,
            .vital-signs-modal:not(.modal-open) .modal-header {
              opacity: 0 !important;
              transform: translateY(10px) !important;
            }

            /* Perfect centering for all screen sizes */
            .vital-signs-modal {
              display: grid !important;
              place-items: center !important;
              min-height: 100vh !important;
              min-width: 100vw !important;
            }

            /* Mobile Responsiveness */
            @media (max-width: 768px) {
              .vital-signs-modal {
                padding: 8px !important;
              }

              .vital-signs-modal > div {
                max-width: calc(100vw - 16px) !important;
                max-height: calc(100vh - 16px) !important;
                width: calc(100vw - 16px) !important;
                padding: 20px !important;
              }

              .vital-signs-grid {
                grid-template-columns: 1fr !important;
                gap: 16px !important;
              }

              .pain-scale-grid {
                grid-template-columns: repeat(auto-fit, minmax(50px, 1fr)) !important;
                gap: 6px !important;
              }

              .pain-scale-button {
                padding: 8px 4px !important;
              }

              .pain-scale-button > div:nth-child(1) {
                font-size: 14px !important;
              }

              .pain-scale-button > div:nth-child(2) {
                font-size: 12px !important;
              }

              .pain-scale-button > div:nth-child(3) {
                font-size: 8px !important;
              }
            }

            @media (max-width: 480px) {
              .vital-signs-modal {
                padding: 4px !important;
              }

              .vital-signs-modal > div {
                max-width: calc(100vw - 8px) !important;
                max-height: calc(100vh - 8px) !important;
                width: calc(100vw - 8px) !important;
                padding: 16px !important;
              }

              .modal-header h2 {
                font-size: 20px !important;
              }

              .form-input {
                padding: 10px 12px !important;
              }

              .pain-scale-section {
                padding: 16px !important;
              }

              .pain-scale-title {
                font-size: 16px !important;
              }

              .action-buttons {
                flex-direction: column !important;
              }

              .action-buttons button {
                width: 100% !important;
              }
            }
          `}
        </style>
      </div>
    </div>
  );

  // Render in portal to body
  return createPortal(modalContent, document.body);
};

export default InpatientVitalSignsModal;

